import React, { useEffect, useState } from 'react';
import { Button, Table, Modal, Space } from 'antd';
import 'antd/dist/reset.css'; // Ensure you import 'antd.css' for the full styles
import { FilePdfFilled } from '@ant-design/icons';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';

import { fetchProviders, setInvoiceCredit } from '../features/providers/providersSlice';

import { useProviderContext } from '../components/ProviderContext';

interface OrderHistoryRecord {
  key: string;
  date: string;
  frequency: string;
  added: number;
  remaining: number;
}

// Sample data for the order history table
const orderHistoryData: OrderHistoryRecord[] = [
  // The existing array objects
  {
    key: "2",
    date: "21.02.2025",
    frequency: "Yearly",
    added: 350,
    remaining: 794
  },
  // Add the rest of your objects here
  {
    key: "11",
    date: "21.02.2034",
    frequency: "Yearly",
    added: 800,
    remaining: 6194
  },
  {
    key: "10",
    date: "21.02.2033",
    frequency: "Yearly",
    added: 750,
    remaining: 0
  },
  {
    key: "12",
    date: "21.02.2033",
    frequency: "Yearly",
    added: 750,
    remaining: 51394
  },
  {
    key: "13",
    date: "21.02.2033",
    frequency: "Yearly",
    added: 750,
    remaining: 0
  },
  {
    key: "14",
    date: "21.02.2033",
    frequency: "Yearly",
    added: 750,
    remaining: 0
  },
  {
    key: "10",
    date: "21.02.2033",
    frequency: "Yearly",
    added: 750,
    remaining: 5394
  },
];

// Configuration for table columns
const columns = [
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
  },
  {
    title: 'Frequency',
    dataIndex: 'frequency',
    key: 'frequency',
  },
  {
    title: 'Added',
    dataIndex: 'added',
    key: 'added',
  },
  {
    title: 'Remaining Days',
    dataIndex: 'remaining',
    key: 'remaining',
    render: (remaining: number) => {
      const color = remaining < 1 ? 'red' : 'green';
      const dayText = remaining === 1 ? 'day' : 'days';
      return (
        <span style={{ color }}>
          {remaining} {dayText}
        </span>
      );
    },
  },
];

const FormInvoices: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const providersStatus = useSelector((state: RootState) => state.providers.status);
  const { selectedProviderId } = useProviderContext();

  const providers = useSelector((state: RootState) =>
    state.providers.data.find(prov => prov.centre_id === selectedProviderId)
  );

  const [invoiceCount, setInvoiceCount] = useState(providers?.Subscription?.invoiceCountLeft || 0);

  useEffect(() => {
    if (providersStatus === 'idle') {
      dispatch(fetchProviders());
    }
  }, [dispatch, providersStatus]);

  useEffect(() => {
    if (providers) {
      setInvoiceCount(providers?.Subscription?.invoiceCountLeft || 0);
    }
  }, [providers]);

  const showModal = () => setIsModalVisible(true);
  const closeModal = () => setIsModalVisible(false);

  const addInvoice = (value: number) => {
    setInvoiceCount(invoiceCount + value);
  };

  const removeInvoice = (value: number) => {
    if (invoiceCount > 0 && invoiceCount - value > 0) {
      setInvoiceCount(invoiceCount - value);
    }
  };

  const handleAddInvoiceCredit = async () => {
    await dispatch(setInvoiceCredit({ subscriptionId: providers?.Subscription.subscription_id, amount: invoiceCount }));
    dispatch(fetchProviders());
  };

  // Styles
  const borderStyle = {
    borderRadius: "8px",
    border: "1px solid rgba(0, 0, 0, 0.06)",
    maxWidth: '1386px', maxHeight: '725px'
  };

  const borderStyle_Card = {
    borderRadius: "8px",
    border: "1px solid rgba(0, 0, 0, 0.06)",
    width: '430px', height: '141px',
  };

  const subscriptionHistoryLayout = {
    display: 'flex',
    gap: '64px',
    alignSelf: 'stretch',
  };

  const headingStyle = {
    color: "var(--colorTextHeading, rgba(0, 0, 0, 0.88))",
    fontFamily: 'SF Pro Text',
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    flex: '1 0 0',
    marginBottom: '32px'
  };

  const creditInvoice_Text = {
    color: "var(--colorTextDescription, rgba(0, 0, 0, 0.45))",
    fontFamily: 'SF Pro Text',
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "22px"
  };

  const invoice_Counts = {
    color: "var(--colorText, rgba(0, 0, 0, 0.88))",
    fontFamily: 'SF Pro Text',
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "32px"
  };

  return (
    <div style={borderStyle}>
      <div style={{ padding: '32px' }}>
        <div style={subscriptionHistoryLayout}>
          <div style={{ flex: 1, maxWidth: '629px', maxHeight: '587px' }}>
            <div style={headingStyle}>Invoices</div>
            <div style={borderStyle_Card}>
              <div style={{ maxWidth: '366px', height: '77px', padding: '32px' }}>
                <div style={{ display: 'flex', gap: '32px' }}>
                  <FilePdfFilled style={{ fontSize: '32px', color: '#1677ff' }} />
                  <div>
                    <div style={creditInvoice_Text}>Credit Invoices</div>
                    <div style={invoice_Counts}>{invoiceCount}</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ paddingTop: '32px' }}>
              <Button type="primary" style={{ width: '236px', height: '32px' }} onClick={showModal}>
                + Add Credit Invoice
              </Button>

              <Modal
                title="Add Invoice Amount"
                open={isModalVisible}
                onCancel={closeModal}
                footer={null}
              >
                <Space>
                  <Button shape='circle' style={{ height: '80px', width: '80px' }} onClick={() => addInvoice(10)}>10</Button>
                  <Button shape='circle' style={{ height: '80px', width: '80px' }} onClick={() => addInvoice(30)}>30</Button>
                  <Button shape='circle' style={{ height: '80px', width: '80px' }} onClick={() => addInvoice(100)}>100</Button>
                  <Button shape='circle' style={{ height: '80px', width: '80px' }} onClick={() => removeInvoice(30)}>- 30</Button>
                  <Button shape='circle' style={{ height: '80px', width: '80px' }} onClick={() => removeInvoice(10)}>-10</Button>
                </Space>
              </Modal>
            </div>
          </div>

          <div style={{ flex: 1, maxWidth: '629px', maxHeight: '587px' }}>
            <div style={{ maxWidth: '629px', height: '517px' }}>
              <div style={headingStyle}>Order History</div>
              <Table dataSource={orderHistoryData} columns={columns} pagination={{ pageSize: 5 }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button style={{ margin: '0 8px' }}>Cancel</Button>
              <Button type="primary" onClick={handleAddInvoiceCredit}>
                Save changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormInvoices;
